import { useState, useEffect, useRef } from 'react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Progress } from '../../ui/progress';
import { ScanLine, Camera, Plus, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';
const POLL_INTERVAL = 2500;
const POLL_MAX = 60000;

const ExtractedFieldsSummary = ({ fields, confidence }) => (
  <div className="space-y-2" data-testid="extracted-fields-summary">
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-500">Extraction confidence:</span>
      <Progress value={(typeof confidence === 'number' ? confidence : 0) * (confidence <= 1 ? 100 : 1)} className="h-1.5 flex-1" />
      <span className="text-xs font-medium text-slate-700">
        {confidence <= 1 ? Math.round(confidence * 100) : Math.round(confidence)}%
      </span>
    </div>
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {fields.first_name && <p><span className="text-slate-500">First:</span> {fields.first_name}</p>}
        {fields.last_name && <p><span className="text-slate-500">Last:</span> {fields.last_name}</p>}
        {fields.date_of_birth && <p><span className="text-slate-500">DOB:</span> {fields.date_of_birth}</p>}
        {fields.gender && <p><span className="text-slate-500">Gender:</span> {fields.gender}</p>}
        {fields.street && (
          <p className="col-span-2"><span className="text-slate-500">Address:</span> {fields.street}{fields.city ? `, ${fields.city}` : ''} {fields.state} {fields.zip_code}</p>
        )}
        {fields.payer_name && <p><span className="text-slate-500">Payer:</span> {fields.payer_name}</p>}
        {fields.member_id && <p><span className="text-slate-500">Member ID:</span> {fields.member_id}</p>}
        {fields.document_type && <p><span className="text-slate-500">Doc type:</span> {fields.document_type}</p>}
        {!fields.first_name && !fields.last_name && <p className="col-span-2 text-slate-400 italic">No fields extracted</p>}
      </div>
    </div>
  </div>
);

const ScanIDModal = ({ open, onOpenChange, onPrefill, onSuccess, initialFile }) => {
  const { authFetch } = useAuth();
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const [step, setStep] = useState('pick');
  const [file, setFile] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [extractedFields, setExtractedFields] = useState(null);
  const [dupResult, setDupResult] = useState(null);
  const [createdPatient, setCreatedPatient] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [pollElapsed, setPollElapsed] = useState(0);
  const initialFileConsumed = useRef(false);

  useEffect(() => {
    if (open) {
      setStep('pick'); setFile(null); setAnalysisId(null);
      setExtractedFields(null); setDupResult(null); setCreatedPatient(null);
      setErrorMsg(''); setPollElapsed(0);
      initialFileConsumed.current = false;
    }
    return () => { clearInterval(pollRef.current); };
  }, [open]);

  useEffect(() => {
    if (open && initialFile && !initialFileConsumed.current) {
      initialFileConsumed.current = true;
      uploadAndProcess(initialFile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFile]);

  const uploadAndProcess = async (f) => {
    setFile(f);
    setStep('uploading');
    setErrorMsg('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await authFetch(`${API}/api/patient-documents/upload`, {
        method: 'POST',
        body: fd,
      });
      if (res.status === 429) { failWith('Too many requests, please wait and try again.'); return; }
      if (res.status === 401) { failWith('Authentication required. Please sign in again.'); return; }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        failWith(err.detail || `Upload failed (${res.status})`);
        return;
      }
      const data = await res.json();
      const aId = data.analysis_id || data.id || data.data?.analysis_id || data.data?.id;
      if (!aId) {
        failWith('Upload succeeded but no analysis ID returned. Try again.');
        return;
      }
      setAnalysisId(aId);
      setStep('processing');
      startPolling(aId);
    } catch {
      failWith('Network error uploading document.');
    }
  };

  const handleFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'pdf'].includes(ext)) {
      toast.error('Unsupported file type. Use JPG, PNG, or PDF.');
      return;
    }
    uploadAndProcess(f);
  };

  const startPolling = (aId) => {
    let elapsed = 0;
    setPollElapsed(0);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      elapsed += POLL_INTERVAL;
      setPollElapsed(elapsed);
      if (elapsed > POLL_MAX) {
        clearInterval(pollRef.current);
        failWith('Analysis timed out. The document may still be processing — try again shortly.');
        return;
      }
      try {
        const res = await authFetch(`${API}/api/analyses/${aId}`);
        if (res.status === 429) { clearInterval(pollRef.current); failWith('Too many requests, please wait and try again.'); return; }
        if (res.status === 401) { clearInterval(pollRef.current); failWith('Session expired. Please sign in again.'); return; }
        if (!res.ok) return;
        const data = await res.json();
        const status = (data.status || '').toLowerCase();
        if (status === 'completed' || status === 'complete' || status === 'done') {
          clearInterval(pollRef.current);
          handleAnalysisComplete(data);
        } else if (status === 'failed' || status === 'error') {
          clearInterval(pollRef.current);
          failWith(data.error || data.detail || 'Document analysis failed. Try a clearer image.');
        }
      } catch {
        // Network blip — keep polling
      }
    }, POLL_INTERVAL);
  };

  const handleAnalysisComplete = async (analysisData) => {
    setStep('extracting');
    const raw = analysisData.extracted_data || analysisData.result || analysisData.data || analysisData.fields || analysisData;
    const fields = {
      first_name: raw.first_name || raw.firstName || '',
      middle_name: raw.middle_name || raw.middleName || '',
      last_name: raw.last_name || raw.lastName || '',
      date_of_birth: raw.date_of_birth || raw.dateOfBirth || raw.dob || '',
      gender: raw.gender || raw.sex || '',
      phone: raw.phone || raw.phone_number || '',
      email: raw.email || '',
      street: raw.street || raw.address || raw.street_address || '',
      city: raw.city || '',
      state: raw.state || '',
      zip_code: raw.zip_code || raw.zip || raw.postal_code || '',
      payer_name: raw.payer_name || raw.insurance_name || raw.payer || raw.insurance || '',
      member_id: raw.member_id || raw.memberId || raw.subscriber_id || '',
      group_number: raw.group_number || raw.groupNumber || raw.group || '',
      document_type: raw.document_type || raw.type || '',
      confidence: raw.confidence || raw.confidence_score || 0,
    };
    setExtractedFields(fields);

    if (fields.first_name && fields.last_name && fields.date_of_birth) {
      try {
        const res = await authFetch(`${API}/api/patients/check-duplicate`, {
          method: 'POST',
          body: JSON.stringify({
            first_name: fields.first_name,
            last_name: fields.last_name,
            date_of_birth: fields.date_of_birth,
          }),
        });
        if (res.ok) {
          const dupData = await res.json();
          setDupResult(dupData);
          if (dupData.duplicate === true && (dupData.matches || []).length > 0) {
            setStep('result_dup');
          } else {
            setStep('result_new');
          }
          return;
        }
      } catch { /* fall through */ }
    }
    setDupResult({ duplicate: false, matches: [] });
    setStep('result_new');
  };

  const handleCreate = async () => {
    if (!extractedFields) return;
    if (!extractedFields.first_name || !extractedFields.last_name || !extractedFields.date_of_birth) {
      toast.error('Cannot create patient: missing required fields (first name, last name, date of birth)');
      return;
    }
    setStep('creating');
    try {
      const payload = { ...extractedFields };
      delete payload.document_type;
      delete payload.confidence;
      const res = await authFetch(`${API}/api/patients`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (res.status === 429) { failWith('Too many requests, please wait and try again.'); return; }
      if (res.ok) {
        const data = await res.json();
        setCreatedPatient(data);
        setStep('created');
        toast.success(`Patient ${extractedFields.first_name} ${extractedFields.last_name} created`);
        onSuccess?.();
      } else {
        const err = await res.json().catch(() => ({}));
        failWith(err.detail || `Failed to create patient (${res.status})`);
      }
    } catch {
      failWith('Network error creating patient.');
    }
  };

  const failWith = (msg) => {
    setErrorMsg(msg);
    setStep('failed');
    clearInterval(pollRef.current);
  };

  const confidence = extractedFields?.confidence || 0;
  const matches = dupResult?.matches || [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) clearInterval(pollRef.current); onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="scan-id-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScanLine className="h-5 w-5" /> Scan ID / Insurance Card</DialogTitle>
          <DialogDescription>Upload a photo of a driver's license, state ID, or insurance card</DialogDescription>
        </DialogHeader>

        {step === 'pick' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center hover:border-slate-400 transition-colors cursor-pointer"
              onClick={() => fileRef.current?.click()} data-testid="scan-dropzone">
              <Camera className="h-8 w-8 text-slate-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-700">Click to upload card image</p>
              <p className="text-xs text-slate-500 mt-1">Supported: JPG, PNG, PDF</p>
            </div>
            <input ref={fileRef} type="file" className="hidden" accept=".jpg,.jpeg,.png,.pdf" onChange={handleFilePick} data-testid="scan-file-input" />
          </div>
        )}

        {step === 'uploading' && (
          <div className="py-8 text-center" data-testid="scan-uploading">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-3" />
            <p className="text-sm text-slate-600">Uploading {file?.name}...</p>
          </div>
        )}

        {step === 'processing' && (
          <div className="py-8 text-center space-y-3" data-testid="scan-processing">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto" />
            <p className="text-sm text-slate-600">Analyzing document...</p>
            <Progress value={Math.min((pollElapsed / POLL_MAX) * 100, 95)} className="h-1.5 max-w-[200px] mx-auto" />
            <p className="text-[10px] text-slate-400">This usually takes 5–15 seconds</p>
          </div>
        )}

        {step === 'extracting' && (
          <div className="py-8 text-center" data-testid="scan-extracting">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-3" />
            <p className="text-sm text-slate-600">Checking for existing patient...</p>
          </div>
        )}

        {step === 'result_dup' && extractedFields && (
          <div className="space-y-4" data-testid="scan-result-duplicate">
            <ExtractedFieldsSummary fields={extractedFields} confidence={confidence} />
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="scan-duplicate-found">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <p className="text-xs font-semibold text-amber-800">Existing patient found</p>
              </div>
              {matches.map((m, i) => {
                const mName = m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim();
                return (
                  <div key={i} className="flex items-center justify-between mt-2">
                    <p className="text-xs text-amber-700">{mName} {m.mrn ? `— ${m.mrn}` : ''}</p>
                    <Button size="sm" variant="outline" className="text-xs h-7"
                      onClick={() => onOpenChange(false)}
                      data-testid={`open-existing-patient-${i}`}>
                      Open Patient
                    </Button>
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setStep('pick'); setFile(null); }}>Scan Another</Button>
              <Button className="bg-black hover:bg-gray-800 gap-2 text-xs" data-testid="prefill-add-patient-btn"
                onClick={() => onPrefill?.(extractedFields)}>
                <Plus className="h-3.5 w-3.5" /> Create Anyway
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'result_new' && extractedFields && (() => {
          const hasRequired = extractedFields.first_name && extractedFields.last_name && extractedFields.date_of_birth;
          return (
          <div className="space-y-4" data-testid="scan-result-new">
            <ExtractedFieldsSummary fields={extractedFields} confidence={confidence} />
            {hasRequired ? (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3" data-testid="scan-new-patient">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <p className="text-xs font-semibold text-green-800">No existing match — ready to create</p>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="scan-missing-fields-warning">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <p className="text-xs font-semibold text-red-800">Missing required fields from scan</p>
                </div>
                <p className="text-xs text-red-600 mt-1">
                  First name, last name, and date of birth are required to create a patient. Re-scan a clearer image or add the patient manually.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setStep('pick'); setFile(null); }} data-testid="scan-try-again-btn">
                Re-scan
              </Button>
              <Button variant="outline" onClick={() => onPrefill?.(extractedFields)} data-testid="prefill-add-patient-btn">
                {hasRequired ? 'Edit Before Creating' : 'Add Manually'}
              </Button>
              {hasRequired && (
                <Button className="bg-black hover:bg-gray-800 gap-2" data-testid="auto-create-patient-btn"
                  onClick={handleCreate}>
                  <Plus className="h-4 w-4" /> Create Patient
                </Button>
              )}
            </DialogFooter>
          </div>
          );
        })()}

        {step === 'creating' && (
          <div className="py-8 text-center" data-testid="scan-creating">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-3" />
            <p className="text-sm text-slate-600">Creating patient...</p>
          </div>
        )}

        {step === 'created' && (
          <div className="space-y-4" data-testid="scan-created">
            <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-center">
              <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm font-semibold text-green-800">Patient Created</p>
              <p className="text-xs text-green-700 mt-1">
                {extractedFields?.first_name} {extractedFields?.last_name}
                {createdPatient?.id ? ` — ID: ${createdPatient.id}` : ''}
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} className="bg-black hover:bg-gray-800" data-testid="scan-done-btn">Done</Button>
            </DialogFooter>
          </div>
        )}

        {step === 'failed' && (
          <div className="space-y-4" data-testid="scan-failed">
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-center">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-red-800">Scan Failed</p>
              <p className="text-xs text-red-600 mt-1">{errorMsg}</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setStep('pick'); setFile(null); setErrorMsg(''); }}>
                Try Again
              </Button>
              <Button className="bg-black hover:bg-gray-800 gap-2" data-testid="manual-add-patient-btn"
                onClick={() => { onOpenChange(false); onPrefill?.({}); }}>
                <Plus className="h-4 w-4" /> Add Manually
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScanIDModal;
